<script lang="ts">
  /**
   * The LP briefing: a seven-section report with a web view and a paginated doc
   * view, per-section editing and print-to-PDF.
   *
   * Page-agnostic on purpose — it renders the `briefing-v1` contract and calls
   * back for everything else, so the Sales client page can mount it with its own
   * API without this file learning about Finance. No business text lives here:
   * every name, number and sentence arrives in `briefing`, `lpName` or `gpName`.
   *
   * The doc view measures before it paginates. Each flow item is rendered once in
   * a hidden page-shaped column, its height read, and `paginate` assigns items to
   * pages from those heights — the rules live in `paginate.ts` so they can be
   * tested without a browser, which is the only place heights are real.
   */
  import { untrack } from 'svelte';
  import { createSectionSpy, jumpToSection } from '../sectionSpy.svelte';
  import BriefingBlock from './BriefingBlock.svelte';
  import EditableText from './EditableText.svelte';
  import ExhibitView from './Exhibit.svelte';
  import Provenance from './Provenance.svelte';
  import SectionHeader from './SectionHeader.svelte';
  import Sources from './Sources.svelte';
  import { buildDocItems, continuationOf, paginate } from './paginate';
  import { footnoteNumbers, SUMMARY_OVERRIDE_ID } from './types';
  import type {
    Block, Briefing, RenderCtx, Section, SectionId, Source, SummaryOverride,
  } from './types';

  let {
    briefing = null,
    lpName,
    gpName = '',
    agent = 'Dealton',
    view = $bindable('web'),
    loading = false,
    generating = false,
    error = '',
    lockedBy = '',
    readOnly = false,
    saveState = 'idle',
    onGenerate,
    onSaveSection,
    onRevertSection,
    onOpenSource,
    onCompare,
    onEnterEdit,
    onLeaveEdit,
    onReadingChange,
  }: {
    briefing?: Briefing | null;
    lpName: string;
    gpName?: string;
    agent?: string;
    view?: 'web' | 'doc';
    loading?: boolean;
    generating?: boolean;
    error?: string;
    /** Display name of the teammate holding the edit lock, when it is not us. */
    lockedBy?: string;
    readOnly?: boolean;
    saveState?: 'idle' | 'saving' | 'saved' | 'error';
    onGenerate?: () => void;
    /** `briefingId` is the briefing the edit was typed on, which may no longer be shown. */
    onSaveSection?: (id: SectionId | 'summary', content: unknown, briefingId: string) => void;
    onRevertSection?: (id: SectionId | 'summary') => void;
    onOpenSource?: (source: Source) => void;
    onCompare?: (id: SectionId) => void;
    onEnterEdit?: () => void;
    onLeaveEdit?: () => void;
    onReadingChange?: (label: string) => void;
  } = $props();

  /** A4 at 96dpi. The page box, the measuring column and the zoom-to-fit all
   *  read it, so there is one number rather than three that drift. */
  const PAGE_W = 794;
  /** Page height less the `.brf-page` padding, header and footer. Only used when
   *  the measuring column reports nothing — a hidden-at-first-paint container,
   *  or jsdom, where every height is 0. Without it the doc view would render no
   *  pages at all rather than one unsplit one. */
  const PAGE_BUDGET_FALLBACK = 1007;

  /* ── local edits ──────────────────────────────────────────────────────────
     Typing updates a local copy so text never lags a round trip; the save is
     debounced, and a freshly loaded briefing supersedes whatever was typed. */
  let localSections = $state<Record<string, { headline: string; blocks: Block[] }>>({});
  let localSummary = $state<SummaryOverride | null>(null);
  const pending: Record<string, {
    timer: ReturnType<typeof setTimeout>; content: unknown; briefingId: string;
  }> = {};

  const sections = $derived(
    (briefing?.sections ?? []).map((section) => {
      const edit = localSections[section.id];
      // `edited_by` is ours while the edit is still local: the server copy will
      // carry the real name, but until it comes back "you" is the true answer.
      return edit
        ? { ...section, ...edit, status: 'edited' as const, edited_by: 'you' }
        : section;
    }),
  );
  const summary = $derived.by(() => {
    const base = briefing?.summary;
    if (!base) return null;
    if (!localSummary) return base;
    return {
      ...base,
      headline: localSummary.headline,
      dek: { ...base.dek, text: localSummary.dek },
      status: 'edited' as const,
      edited_by: 'you',
    };
  });
  const summaryEdited = $derived(!!localSummary || summary?.status === 'edited');

  function queueSave(key: SectionId | 'summary', content: unknown) {
    if (!briefing) return;
    const briefingId = briefing.briefing_id;
    clearTimeout(pending[key]?.timer);
    pending[key] = { content, briefingId, timer: setTimeout(() => {
      delete pending[key];
      onSaveSection?.(key, content, briefingId);
    }, 700) };
  }
  function cancelSave(key: string) {
    clearTimeout(pending[key]?.timer);
    delete pending[key];
  }
  /** Sends every queued save now: the lock is about to go, or the view is closing. */
  function flushSaves() {
    for (const [key, entry] of Object.entries(pending)) {
      cancelSave(key);
      onSaveSection?.(key as SectionId | 'summary', entry.content, entry.briefingId);
    }
  }

  function editHeadline(section: Section, headline: string) {
    const next = { headline, blocks: localSections[section.id]?.blocks ?? section.blocks };
    localSections = { ...localSections, [section.id]: next };
    queueSave(section.id, next);
  }
  function editBlock(section: Section, index: number, block: Block) {
    const blocks = (localSections[section.id]?.blocks ?? section.blocks)
      .map((b, i) => (i === index ? block : b));
    const next = { headline: localSections[section.id]?.headline ?? section.headline, blocks };
    localSections = { ...localSections, [section.id]: next };
    queueSave(section.id, next);
  }
  function editSummary(patch: Partial<SummaryOverride>) {
    if (!summary) return;
    const next = {
      headline: patch.headline ?? summary.headline,
      dek: patch.dek ?? summary.dek.text,
    };
    localSummary = next;
    queueSave(SUMMARY_OVERRIDE_ID, next);
  }
  function revert(id: SectionId | 'summary') {
    // A queued save landing after the DELETE would re-create the override.
    cancelSave(id);
    if (id === SUMMARY_OVERRIDE_ID) {
      localSummary = null;
    } else {
      localSections = Object.fromEntries(
        Object.entries(localSections).filter(([key]) => key !== id));
    }
    onRevertSection?.(id);
  }

  let loadedId = $state('');
  $effect(() => {
    if (briefing && briefing.briefing_id !== loadedId) {
      loadedId = briefing.briefing_id;
      localSections = {};
      localSummary = null;
    }
  });

  /* ── render context ──────────────────────────────────────────────────────── */
  const footnotes = $derived(footnoteNumbers(briefing?.sources ?? []));
  const publicSources = $derived((briefing?.sources ?? []).filter(s => s.kind === 'public'));
  const relSources = $derived((briefing?.sources ?? []).filter(s => s.kind !== 'public'));

  let sourcesOpen = $state(false);
  let highlighted = $state('');
  let flashTimer: ReturnType<typeof setTimeout> | undefined;

  function onFootnote(sourceId: string) {
    sourcesOpen = true;
    highlighted = sourceId;
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => (highlighted = ''), 1800);
    const prefix = view === 'doc' ? 'brf-doc' : 'brf-web';
    // The list sits in a `<details>` that may have just been opened, so the
    // scroll has to wait for the browser to lay it out.
    requestAnimationFrame(() => {
      // `?.` on the METHOD as well as the element: jsdom gives elements no
      // scrollIntoView, so guarding only the element throws in every test that
      // clicks a footnote. Same form as FundWorkspace.svelte's scroll helpers.
      document.getElementById(`${prefix}-${sourceId}`)
        ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  }

  function makeCtx(idPrefix: string): RenderCtx {
    return {
      footnotes,
      sources: briefing?.sources ?? [],
      idPrefix,
      // The measuring pass is inert: its footnotes must not be clickable, and
      // its ids are namespaced so they cannot shadow the visible ones.
      onFootnote: idPrefix === 'brf-measure' ? undefined : onFootnote,
      onOpenSource: idPrefix === 'brf-measure' ? undefined : onOpenSource,
    };
  }
  const webCtx = $derived(makeCtx('brf-web'));
  const docCtx = $derived(makeCtx('brf-doc'));
  const measureCtx = $derived(makeCtx('brf-measure'));

  const editing = $derived(view === 'doc' && !readOnly && !lockedBy);
  /** Revert works from either view, but never over a teammate's lock or in a read-only view. */
  const canRevert = $derived(!readOnly && !lockedBy);
  const briefingId = $derived(briefing?.briefing_id ?? '');
  const exhibitNumber = (id: string) => (briefing?.exhibits ?? []).findIndex(e => e.id === id) + 1;
  const exhibitById = (id: string) => (briefing?.exhibits ?? []).find(e => e.id === id);
  const referencedExhibits = $derived(new Set(
    sections.flatMap(s => s.blocks
      .flatMap(b => (b.type === 'exhibit_ref' ? [b.exhibit_id] : []))),
  ));

  /* ── view switching, lock, print ─────────────────────────────────────────── */
  $effect(() => {
    // The print stylesheet keys off this body class: the pages have to escape a
    // shell of scroll containers, which only a global rule can reach.
    document.body.classList.toggle('brf-doc-mode', view === 'doc');
    return () => document.body.classList.remove('brf-doc-mode');
  });
  // The briefing we entered edit mode for. Not reactive: a reload re-sends an
  // equal briefing and can re-run the effect below, which must not drop and
  // retake the lock unless the target itself changed.
  let editingFor = '';
  function leaveEditing() {
    if (!editingFor) return;
    editingFor = '';
    flushSaves();
    onLeaveEdit?.();
  }
  $effect(() => {
    const target = view === 'doc' && !readOnly ? briefingId : '';
    untrack(() => {
      if (target === editingFor) return;
      leaveEditing();
      editingFor = target;
      if (target) onEnterEdit?.();
    });
  });
  $effect(() => () => untrack(leaveEditing));

  let docPagesEl = $state<HTMLElement>();
  // Printing has to isolate the pages from a shell this component knows nothing
  // about, so it marks its own ancestor chain and lets the print rules hide each
  // level's off-path siblings. Nothing here names a host page's classes.
  $effect(() => {
    if (view !== 'doc' || !docPagesEl) return;
    const leaf = docPagesEl;
    leaf.classList.add('brf-doc-leaf');
    const marked: HTMLElement[] = [];
    for (let node = leaf.parentElement; node; node = node.parentElement) {
      node.classList.add('brf-doc-path');
      marked.push(node);
    }
    return () => {
      leaf.classList.remove('brf-doc-leaf');
      for (const node of marked) node.classList.remove('brf-doc-path');
    };
  });

  function exportPdf() {
    view = 'doc';
    // Two frames: one for the pages to mount, one for them to lay out. Printing
    // sooner captures an empty container.
    requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
  }

  /* ── doc pagination ──────────────────────────────────────────────────────── */
  const docItems = $derived(briefing ? buildDocItems(briefing) : []);
  let measureEls = $state<(HTMLElement | undefined)[]>([]);
  let measureBody = $state<HTMLElement>();
  let heights = $state<number[]>([]);
  let budget = $state(0);
  let docWrap = $state<HTMLElement>();
  let scale = $state(1);
  // Compared against, never rendered: reading `heights` inside the measuring
  // effect would make it depend on its own output.
  let measured: number[] = [];

  $effect(() => {
    if (view !== 'doc' || !measureBody) return;
    // Touch what changes a height so the measurement re-runs with the content.
    void sections; void summary; void measureEls;
    // Indexed by `docItems`, not by `measureEls`: the element array keeps its
    // length when the item list shrinks, and a stale tail would leave
    // `heights.length` permanently disagreeing with the flow.
    const next = docItems.map((_, i) => measureEls[i]?.getBoundingClientRect().height ?? 0);
    if (next.length !== measured.length || next.some((h, i) => h !== measured[i])) {
      measured = next;
      heights = next;
    }
    const body = measureBody.clientHeight || PAGE_BUDGET_FALLBACK;
    if (body !== budget) budget = body;
  });

  const pages = $derived(
    view === 'doc' && budget > 0 && heights.length === docItems.length && docItems.length > 0
      ? paginate(docItems, i => heights[i] ?? 0, budget)
      : [],
  );

  $effect(() => {
    if (view !== 'doc' || !docWrap) return;
    const fit = () => {
      const width = docWrap?.clientWidth ?? PAGE_W;
      scale = Math.min(1, (width - 12) / PAGE_W);
    };
    fit();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(docWrap);
    return () => observer?.disconnect();
  });

  type PageEntry =
    | { t: 'lede' } | { t: 'dek' } | { t: 'kpis' } | { t: 'flags' }
    | { t: 'exhibit'; exhibitId: string }
    | { t: 'header'; section: Section; number: number }
    | { t: 'block'; section: Section; number: number; blockIndex: number }
    | { t: 'sources-header' }
    | { t: 'sources'; from: number; items: Source[] };

  /** Resolve a page's measured items into renderable entries. */
  function toEntries(indices: number[]): PageEntry[] {
    const out: PageEntry[] = [];
    for (const index of indices) {
      const item = docItems[index];
      if (!item) continue;
      if (item.kind === 'sources') {
        out.push({ t: 'sources', from: 0, items: publicSources });
        continue;
      }
      if (item.kind === 'section-header' || item.kind === 'block') {
        const at = sections.findIndex(s => s.id === item.sectionId);
        if (at < 0) continue;
        out.push(item.kind === 'section-header'
          ? { t: 'header', section: sections[at], number: at + 1 }
          : { t: 'block', section: sections[at], number: at + 1, blockIndex: item.blockIndex });
        continue;
      }
      if (item.kind === 'exhibit') out.push({ t: 'exhibit', exhibitId: item.exhibitId });
      else if (item.kind === 'sources-header') out.push({ t: 'sources-header' });
      else if (item.kind === 'lede') out.push({ t: 'lede' });
      else if (item.kind === 'dek') out.push({ t: 'dek' });
      else if (item.kind === 'kpis') out.push({ t: 'kpis' });
      else if (item.kind === 'flags') out.push({ t: 'flags' });
    }
    return out;
  }

  /* ── table of contents ───────────────────────────────────────────────────── */
  const spy = createSectionSpy();
  const spyOn = spy.spyOn;
  $effect(() => {
    const active = spy.active;
    const section = sections.find((item) => active === `brf-web-${item.id}`);
    onReadingChange?.(view === 'doc' ? 'Doc view' : section?.question ||
      (active === 'brf-web-sources' ? 'Sources' : 'Summary'));
  });
  const navLabel = (question: string) =>
    question.length <= 34 ? question : `${question.slice(0, 33).replace(/[\s,;:]+\S*$/, '')}…`;
  const jump = (event: MouseEvent, id: string) => jumpToSection(event, `brf-web-${id}`);

  const updated = $derived.by(() => {
    if (!briefing) return '';
    const when = new Date(briefing.generated_at);
    const days = Math.floor((Date.now() - when.getTime()) / 86_400_000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days} days ago`;
    return when.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  });
  /** Says what the briefing is based on, never that anything read a whole
   *  mailbox. The counts come from `basis`; no count is no claim. */
  const basisLine = $derived.by(() => {
    const b = briefing?.basis;
    if (!b) return '';
    const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
    const parts = [
      b.threads ? plural(b.threads, 'thread') : '',
      b.meetings ? plural(b.meetings, 'meeting') : '',
      b.notes ? plural(b.notes, 'note') : '',
      b.public_sources ? plural(b.public_sources, 'public source') : '',
    ].filter(Boolean);
    return parts.length ? `Based on ${parts.join(', ')}` : '';
  });
  const docDate = $derived(briefing
    ? new Date(briefing.generated_at)
        .toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '');
</script>

<!-- ───────────────────────── content snippets ─────────────────────────
     One snippet per piece of content, shared by the web article, the doc pages
     and the measuring column, so the three can never drift apart. `live` is
     false for the measuring pass: no editing, no jump targets. -->
{#snippet ledeView(live: boolean)}
  {#if summary}
    <h2 class="brf-lede"><EditableText value={summary.headline} editing={live && editing}
      ariaLabel="Briefing recommendation"
      onchange={(headline) => editSummary({ headline })} />{#if summaryEdited}<span
        class="brf-edmark" contenteditable="false">Edited by {localSummary
          ? 'you' : (summary.edited_by || 'a teammate')}{#if canRevert}<button type="button"
          onclick={() => revert(SUMMARY_OVERRIDE_ID)}>Revert</button>{/if}</span>{/if}</h2>
  {/if}
{/snippet}

{#snippet dekView(ctx: RenderCtx, live: boolean)}
  {#if summary}
    <p class="brf-dek"><EditableText value={summary.dek.text} editing={live && editing}
      ariaLabel="How to do it" onchange={(dek) => editSummary({ dek })} /><Provenance
      provenance={summary.dek.provenance} sourceIds={summary.dek.source_ids} {ctx} /></p>
  {/if}
{/snippet}

{#snippet kpisView(ctx: RenderCtx)}
  {#if summary}
    <dl class="brf-kpis">
      {#each summary.kpis as kpi, i (i)}
        <div class="brf-kpi">
          <dt>{kpi.value}</dt>
          <dd>{kpi.label}<Provenance provenance={kpi.provenance}
            sourceIds={kpi.source_ids} {ctx} /></dd>
        </div>
      {/each}
    </dl>
  {/if}
{/snippet}

{#snippet flagsView(live: boolean)}
  {#if summary?.flags.length}
    <div class="brf-flags">
      <span class="brf-flags-h"><span class="spark" aria-hidden="true">✦</span> Before you read</span>
      <!-- Model text is not a unique key; a repeated one throws in production. -->
      {#each summary.flags as flag, i (i)}
        {#if live}
          <button type="button" class="brf-flag-i" onclick={(event) => jump(event, flag.section)}
            ><b>{flag.label}</b>{#if flag.detail} · {flag.detail}{/if}</button>
        {:else}
          <span class="brf-flag-i"><b>{flag.label}</b>{#if flag.detail} · {flag.detail}{/if}</span>
        {/if}
      {/each}
    </div>
  {/if}
{/snippet}

{#snippet exhibitView(exhibitId: string, ctx: RenderCtx)}
  {@const exhibit = exhibitById(exhibitId)}
  {#if exhibit}
    <ExhibitView {exhibit} {ctx} number={exhibitNumber(exhibitId)} />
  {/if}
{/snippet}

{#snippet headerView(section: Section, number: number, live: boolean, continuation: boolean)}
  <SectionHeader {section} {number} {agent} editing={live && editing} {continuation}
    onheadline={(text) => editHeadline(section, text)}
    onrevert={canRevert && (section.status === 'edited' || section.status === 'stale')
      ? () => revert(section.id) : undefined}
    oncompare={section.status === 'stale' && onCompare ? () => onCompare?.(section.id) : undefined} />
{/snippet}

{#snippet blockView(section: Section, index: number, ctx: RenderCtx, live: boolean)}
  {@const block = section.blocks[index]}
  {#if block && block.type === 'exhibit_ref'}
    {@render exhibitView(block.exhibit_id, ctx)}
  {:else if block}
    <BriefingBlock {block} {ctx} editing={live && editing}
      onchange={(next) => editBlock(section, index, next)} />
  {/if}
{/snippet}

{#snippet entriesView(entries: PageEntry[], ctx: RenderCtx, live: boolean)}
  {#each entries as entry, e (e)}
    {#if entry.t === 'lede'}{@render ledeView(live)}
    {:else if entry.t === 'dek'}{@render dekView(ctx, live)}
    {:else if entry.t === 'kpis'}{@render kpisView(ctx)}
    {:else if entry.t === 'flags'}{@render flagsView(false)}
    {:else if entry.t === 'exhibit'}{@render exhibitView(entry.exhibitId, ctx)}
    {:else if entry.t === 'header'}
      {@const section = entry.section}
      <div class="brf-sec">{@render headerView(section, entry.number, live, false)}</div>
    {:else if entry.t === 'block'}
      {@const section = entry.section}
      <div class="brf-sec-b">{@render blockView(section, entry.blockIndex, ctx, live)}</div>
    {:else if entry.t === 'sources-header'}
      <p class="brf-srcs-h">Sources</p>
    {:else if entry.t === 'sources'}
      <Sources items={entry.items} {ctx} startNumber={entry.from + 1} collapsible={false} />
    {/if}
  {/each}
{/snippet}

<!-- ───────────────────────── shell ───────────────────────── -->
<div class="brf" class:brf-is-doc={view === 'doc'}>
  {#if error}<p class="brf-alert" role="alert">{error}</p>{/if}

  {#if !briefing}
    <div class="empty-state brf-empty">
      {#if loading || generating}
        {#if generating}
          <p class="brf-empty-h">{agent} is writing the briefing…</p>
          <p class="brf-empty-p">Sections appear as they finish.</p>
        {:else}
          <p class="brf-empty-h" role="status">Loading the briefing…</p>
        {/if}
        <div class="brf-skeleton" aria-hidden="true">
          {#each [1, 2, 3, 4] as row (row)}
            <div class="brf-sk-row"><span></span><span></span><span></span></div>
          {/each}
        </div>
      {:else}
        <p class="brf-empty-h">No briefing yet</p>
        <p class="brf-empty-p">
          {agent} answers seven questions about {lpName} from the synced email
          threads and team notes your team has with them. Every claim carries its
          source.
        </p>
        {#if onGenerate}
          <button type="button" class="btn-primary" onclick={onGenerate}>Generate briefing</button>
        {/if}
      {/if}
    </div>
  {:else}
    <div class="brf-tools">
      <span class="brf-who">
        <span class="spark" aria-hidden="true">✦</span>
        Prepared by {agent} · updated {updated}{#if basisLine}{' · '}{basisLine}{/if}
      </span>
      <span class="brf-legend">
        {#if footnotes.size}<span class="brf-legend-fn"><sup>1</sup> Public source</span>{/if}
        <span class="brf-tag rel">Relationship</span>
        <span class="brf-tag inf">Inference</span>
      </span>
      <span class="brf-tools-r">
        {#if view === 'doc'}
          {#if lockedBy}
            <span class="brf-lock" role="status">{lockedBy} is editing</span>
          {:else if readOnly}
            <span class="brf-lock" role="status">Read-only</span>
          {:else}
            <span class="brf-saved" role="status">{saveState === 'saving'
              ? 'Saving…' : saveState === 'error'
              ? 'Not saved' : saveState === 'saved' ? 'Saved' : 'Editable'}</span>
          {/if}
        {/if}
        <!-- `.seg` is the app's one segmented control; `on` is its selected state. -->
        <span class="seg" role="group" aria-label="View">
          <button type="button" class:on={view === 'web'} aria-pressed={view === 'web'}
                  onclick={() => (view = 'web')}>Web</button>
          <button type="button" class:on={view === 'doc'} aria-pressed={view === 'doc'}
                  onclick={() => (view = 'doc')}>Doc</button>
        </span>
        <button type="button" class="btn-primary" onclick={exportPdf}>Export PDF</button>
      </span>
    </div>

    <div class="brf-read">
      <nav class="brf-toc" aria-label="Briefing sections">
        <a href="#brf-web-summary" class:cur={!spy.active || spy.active === 'brf-web-summary'}
           class:ed={summaryEdited} onclick={(event) => jump(event, 'summary')}>Summary</a>
        {#each sections as section, i (section.id)}
          <a href="#brf-web-{section.id}" class:cur={spy.active === `brf-web-${section.id}`}
             class:ed={section.status === 'edited' || section.status === 'stale'}
             title={section.question} onclick={(event) => jump(event, section.id)}
            ><span class="n">{i + 1}</span>{navLabel(section.question)}</a>
        {/each}
        <a href="#brf-web-sources" class:cur={spy.active === 'brf-web-sources'}
           onclick={(event) => jump(event, 'sources')}>Sources</a>
      </nav>

      <div class="brf-content" bind:this={docWrap}>
        {#if view === 'web'}
          <article class="brf-paper">
            <section id="brf-web-summary" class="brf-summary" use:spyOn>
              {@render ledeView(true)}
              {@render dekView(webCtx, true)}
              {@render kpisView(webCtx)}
              {@render flagsView(true)}
              {#each briefing.exhibits as exhibit (exhibit.id)}
                {#if !referencedExhibits.has(exhibit.id)}
                  {@render exhibitView(exhibit.id, webCtx)}
                {/if}
              {/each}
            </section>

            {#each sections as section, i (section.id)}
              <section id="brf-web-{section.id}" class="brf-sec" use:spyOn>
                {@render headerView(section, i + 1, true, false)}
                <div class="brf-sec-b">
                  {#each section.blocks as _block, b (b)}
                    {@render blockView(section, b, webCtx, true)}
                  {/each}
                </div>
              </section>
            {/each}

            <section id="brf-web-sources" class="brf-sec" use:spyOn>
              <Sources items={publicSources} relationship={relSources} ctx={webCtx}
                       total={briefing.sources.length} bind:open={sourcesOpen} {highlighted} />
            </section>
          </article>
        {:else}
          <div class="brf-docpages" bind:this={docPagesEl}>
            {#each pages as page, p (p)}
              {@const cont = continuationOf(docItems, page)}
              <section class="brf-page" style:zoom={scale}
                       aria-label="Page {p + 1} of {pages.length}">
                <div class="brf-ph">
                  <span><b>LP briefing</b> · {lpName}</span>
                  <span>{gpName}{gpName ? ' · ' : ''}{docDate}</span>
                </div>
                <div class="brf-pb">
                  {#if cont}
                    {@const at = sections.findIndex(s => s.id === cont)}
                    {#if at >= 0}
                      <div class="brf-sec">
                        {@render headerView(sections[at], at + 1, true, true)}
                      </div>
                    {/if}
                  {/if}
                  {@render entriesView(toEntries(page), docCtx, true)}
                </div>
                <div class="brf-pf">
                  <span>Confidential · prepared by {agent} for the fundraising team</span>
                  <span>{basisLine ? `${basisLine} · ` : ''}Page {p + 1} of {pages.length}</span>
                </div>
              </section>
            {/each}
            {#if !pages.length}<p class="brf-empty-p">Laying out pages…</p>{/if}
          </div>

          <!-- Hidden measuring pass: one page-shaped column, every item once. -->
          <div class="brf-measure" aria-hidden="true">
            <section class="brf-page">
              <div class="brf-ph"><span><b>LP briefing</b> · {lpName}</span><span>{docDate}</span></div>
              <div class="brf-pb" bind:this={measureBody}>
                {#each docItems as _item, i (i)}
                  <div bind:this={measureEls[i]}>
                    {@render entriesView(toEntries([i]), measureCtx, false)}
                  </div>
                {/each}
              </div>
              <div class="brf-pf"><span>Confidential</span><span>Page 1 of 1</span></div>
            </section>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>
